/**
 * Create a distinct union between arrays
 * @returns {...*}
 * @param arrays
 */
export function union<T>(...arrays: Array<Array<T>>): Array<T> {
    return [ ...(new Set(arrays.reduce((a, b) => [ ...a, ...b ]))) ];
}

/**
 * Intersects arrays together
 * @returns {*[]}
 * @param arrays
 */
export function intersect<T>(...arrays: Array<Array<T>>): Array<T> {
    if(arrays.length < 1) {
        return [];
    }

    let results = [ ...new Set(arrays.shift()) ];

    for(let i = 0, l = arrays.length; i < l; i++) {
        if(results.length === 0) {
            return [];
        }

        results = results.filter(x => new Set(arrays[i]).has(x));
    }

    return results;
}

/**
 * Merge two arrays together
 * @param a
 * @param b
 * @returns {*[]}
 */
export function merge<A, B>(a: Array<A>, b: Array<B>): Array<A | B> {
    return [ ...a, ...b ];
}

/**
 * Returns relative complement of array A in array B (Shallow comparison)
 * @param a
 * @param b
 * @param compare
 */
export function complement<A>(a: Array<A>, b: Array<A>, compare: (i: A, j: A) => boolean): Array<A> {
    if(typeof compare === "undefined") {
        compare = (i, j): boolean => (i !== j);
    }

    return b.filter(i => {
        return a.every(j => {
            return compare(i, j);
        });
    });
}

/**
 * Group an array by the provided key
 * @param data
 * @param key
 * @param addOriginalIndex
 */
export function groupBy<T extends Record<string, any>, AddIndex extends boolean>(
    data: Array<T>,
    key: string,
    addOriginalIndex?: AddIndex
): Record<string, Array<AddIndex extends true ? T & { index: number; } : T>> {
    const grouped: Record<string, Array<T>> = {};

    for(let i = 0, l = data.length; i < l; i++) {
        if(!grouped[data[i][key]]) {
            grouped[data[i][key]] = [];
        }

        const groupData = {
            ...data[i],
        };

        if(addOriginalIndex) {
            (groupData as T & { index: number; }).index = i;
        }

        grouped[data[i][key] as keyof typeof grouped].push(groupData);
    }

    return grouped as Record<string, Array<AddIndex extends true ? T & { index: number; } : T>>;
}

/**
 * Chunk an array into the provided size
 * @param data
 * @param size
 */
export function chunk<T>(data: Array<T>, size: number): Array<Array<T>> {
    const chunked = [];

    for(let i = 0, l = data.length; i < l; i += size) {
        chunked.push(data.slice(i, i + size));
    }

    return chunked;
}

/**
 * Perform an average on the provided array (using mean method)
 * @param data
 */
export function meanArray(data: Array<number>): number {
    let total = 0;

    for(let i = 0, l = data.length; i < l; i++) {
        total += data[i];
    }

    return total / data.length;
}
